import React from 'react';
import styled, { keyframes } from 'styled-components';

import { STAFF_IMPORT_PAGE_COPY } from '../constants/staffImport';
import { staffImportTokens } from '../styles/staffImportTokens';

const SCROLL_CONTENT_HEIGHT = 520;
const FIGMA_SCROLL_TOP = -442;
const FIGMA_SCROLL_START_TRANSLATE = 474;
const SCROLL_ANIMATION_MS = 4000;
const FIGMA_ILLUSTRATION_WIDTH = 421;
const FIGMA_ILLUSTRATION_HEIGHT = 311;
const FIGMA_MONITOR_OFFSET_LEFT = 70;
const FIGMA_MONITOR_OFFSET_TOP = 60;

const scrollTranslate = keyframes`
    0% {
        transform: translateY(${FIGMA_SCROLL_START_TRANSLATE}px);
    }
    100% {
        transform: translateY(0);
    }
`;

const scrollOpacity = keyframes`
    0% {
        opacity: 1;
    }
    12.5% {
        opacity: 0.4;
    }
    25% {
        opacity: 1;
    }
    37.5% {
        opacity: 0.4;
    }
    50% {
        opacity: 1;
    }
    62.5% {
        opacity: 0.4;
    }
    75% {
        opacity: 1;
    }
    87.5% {
        opacity: 0.4;
    }
    100% {
        opacity: 1;
    }
`;

const ScanningPanelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    width: 100%;
`;

const MonitorIllustration = styled.div`
    position: absolute;
    left: ${FIGMA_MONITOR_OFFSET_LEFT}px;
    top: ${FIGMA_MONITOR_OFFSET_TOP}px;
    width: 280px;
    height: 200px;
    border: 2px solid #a6a6b2;
    border-radius: 8px;
    background: #ffffff;
    overflow: hidden;
`;

const MonitorTopBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
    height: 24px;
    border-bottom: 1px solid #a6a6b2;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececf1 100%);
`;

const ScrollingTrack = styled.div`
    position: absolute;
    left: 8px;
    top: ${FIGMA_SCROLL_TOP}px;
    z-index: 0;
    width: 260px;
    animation:
        ${scrollTranslate} ${SCROLL_ANIMATION_MS}ms linear infinite,
        ${scrollOpacity} ${SCROLL_ANIMATION_MS}ms ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
        transform: translateY(${FIGMA_SCROLL_START_TRANSLATE}px);
    }
`;

const ScrollContentPane = styled.div`
    position: relative;
    width: 260px;
    height: ${SCROLL_CONTENT_HEIGHT}px;
`;

const SkeletonBlock = styled.div<{
    $width?: string;
    $height?: string;
    $top?: string;
    $left?: string;
    $opacity?: number;
    $borderRadius?: string;
    $border?: string;
}>`
    position: absolute;
    top: ${({ $top }) => $top || '0'};
    left: ${({ $left }) => $left || '0'};
    width: ${({ $width }) => $width || '100%'};
    height: ${({ $height }) => $height || '6px'};
    border-radius: ${({ $borderRadius }) => $borderRadius || '2px'};
    border: ${({ $border }) => $border || 'none'};
    background: rgba(166, 166, 178, ${({ $opacity }) => $opacity ?? 0.25});
`;

const SkeletonCard = styled.div<{ $left: string; $top?: string }>`
    position: absolute;
    top: ${({ $top }) => $top || '128px'};
    left: ${({ $left }) => $left};
    width: 73px;
    height: 65px;
    border: 1px solid rgba(166, 166, 178, 0.25);
    border-radius: 4px;
    overflow: hidden;
`;

const SkeletonCardImage = styled.div`
    position: absolute;
    top: 4px;
    left: 4px;
    width: 63px;
    height: 30px;
    border-radius: 3px;
    background: rgba(224, 224, 229, 0.3);
`;

const SkeletonCardLine = styled.div<{ $width: string; $top: string }>`
    position: absolute;
    left: 4px;
    top: ${({ $top }) => $top};
    width: ${({ $width }) => $width};
    height: 5px;
    border-radius: 2px;
    background: rgba(166, 166, 178, 0.2);
`;

const SkeletonColumn = styled.div<{ $left: string }>`
    position: absolute;
    top: 290px;
    left: ${({ $left }) => $left};
    width: 115px;
    height: 80px;
    border: 1px solid rgba(166, 166, 178, 0.2);
    border-radius: 4px;
    overflow: hidden;
`;

const MonitorStandNeck = styled.div`
    position: absolute;
    left: ${FIGMA_MONITOR_OFFSET_LEFT + 138}px;
    top: ${FIGMA_MONITOR_OFFSET_TOP + 200}px;
    width: 4px;
    height: 35px;
    background: #a6a6b2;
`;

const MonitorStandBase = styled.div`
    position: absolute;
    left: ${FIGMA_MONITOR_OFFSET_LEFT + 100}px;
    top: ${FIGMA_MONITOR_OFFSET_TOP + 232}px;
    width: 80px;
    height: 6px;
    border-radius: 3px;
    background: #a6a6b2;
`;

const IllustrationWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
    width: ${FIGMA_ILLUSTRATION_WIDTH}px;
    height: ${FIGMA_ILLUSTRATION_HEIGHT}px;
`;

const ScanningCopy = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: ${staffImportTokens.spacing.fieldGap}px;
    min-width: 0;
`;

const ScanningTitle = styled.h2`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: 21px;
    font-weight: 600;
    line-height: 1.5;
    color: ${staffImportTokens.colors.textPrimary};
`;

const ScanningMessage = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: normal;
    color: ${staffImportTokens.colors.textPrimary};
`;

const MonitorScrollSkeleton: React.FC = () => (
    <>
        <SkeletonBlock $width="30px" $top="8px" $left="10px" />
        <SkeletonBlock $width="30px" $top="8px" $left="50px" />
        <SkeletonBlock $width="30px" $top="8px" $left="90px" />
        <SkeletonBlock $width="30px" $top="8px" $left="130px" />
        <SkeletonBlock
            $width="240px"
            $height="70px"
            $top="25px"
            $left="10px"
            $opacity={0.35}
            $borderRadius="4px"
            $border="1px solid rgba(166, 166, 178, 0.25)"
        />
        <SkeletonBlock
            $width="120px"
            $height="8px"
            $top="45px"
            $left="70px"
            $opacity={0.2}
        />
        <SkeletonBlock
            $width="80px"
            $height="8px"
            $top="59px"
            $left="70px"
            $opacity={0.2}
        />
        <SkeletonBlock
            $width="100px"
            $height="10px"
            $top="110px"
            $left="10px"
            $opacity={0.3}
            $borderRadius="3px"
        />
        <SkeletonCard $left="10px">
            <SkeletonCardImage />
            <SkeletonCardLine $width="50px" $top="39px" />
            <SkeletonCardLine $width="35px" $top="49px" />
        </SkeletonCard>
        <SkeletonCard $left="90px">
            <SkeletonCardImage />
            <SkeletonCardLine $width="50px" $top="39px" />
            <SkeletonCardLine $width="35px" $top="49px" />
        </SkeletonCard>
        <SkeletonCard $left="170px">
            <SkeletonCardImage />
            <SkeletonCardLine $width="50px" $top="39px" />
            <SkeletonCardLine $width="35px" $top="49px" />
        </SkeletonCard>
        <SkeletonBlock
            $width="120px"
            $height="10px"
            $top="210px"
            $left="10px"
            $opacity={0.3}
            $borderRadius="3px"
        />
        <SkeletonBlock
            $width="230px"
            $top="230px"
            $left="10px"
            $opacity={0.15}
        />
        <SkeletonBlock
            $width="230px"
            $top="242px"
            $left="10px"
            $opacity={0.15}
        />
        <SkeletonBlock
            $width="230px"
            $top="254px"
            $left="10px"
            $opacity={0.15}
        />
        <SkeletonBlock
            $width="160px"
            $top="266px"
            $left="10px"
            $opacity={0.15}
        />
        <SkeletonColumn $left="10px">
            <SkeletonBlock
                $width="100px"
                $height="40px"
                $top="6px"
                $left="6px"
                $opacity={0.3}
                $borderRadius="3px"
            />
            <SkeletonBlock
                $width="70px"
                $height="5px"
                $top="53px"
                $left="6px"
                $opacity={0.18}
            />
            <SkeletonBlock
                $width="50px"
                $height="5px"
                $top="63px"
                $left="6px"
                $opacity={0.18}
            />
        </SkeletonColumn>
        <SkeletonColumn $left="135px">
            <SkeletonBlock
                $width="100px"
                $height="40px"
                $top="6px"
                $left="6px"
                $opacity={0.3}
                $borderRadius="3px"
            />
            <SkeletonBlock
                $width="70px"
                $height="5px"
                $top="53px"
                $left="6px"
                $opacity={0.18}
            />
            <SkeletonBlock
                $width="50px"
                $height="5px"
                $top="63px"
                $left="6px"
                $opacity={0.18}
            />
        </SkeletonColumn>
        <SkeletonBlock
            $width="80px"
            $height="18px"
            $top="390px"
            $left="90px"
            $opacity={0.1}
            $borderRadius="9px"
            $border="1px solid rgba(166, 166, 178, 0.3)"
        />
        <SkeletonBlock
            $width="140px"
            $top="430px"
            $left="40px"
            $opacity={0.1}
            $height="5px"
        />
        <SkeletonBlock
            $width="180px"
            $top="440px"
            $left="40px"
            $opacity={0.1}
            $height="5px"
        />
        <SkeletonBlock
            $width="140px"
            $top="450px"
            $left="40px"
            $opacity={0.1}
            $height="5px"
        />
    </>
);

export interface StaffImportScanningPanelProps {
    title?: string;
    message?: string;
}

export const StaffImportScanningPanel: React.FC<
    StaffImportScanningPanelProps
> = ({
    title = STAFF_IMPORT_PAGE_COPY.scanningTitle,
    message = STAFF_IMPORT_PAGE_COPY.scanningMessage,
}) => (
    <ScanningPanelContainer aria-live="polite" aria-busy="true">
        <IllustrationWrapper aria-hidden="true">
            <MonitorIllustration>
                <ScrollingTrack data-testid="staff-import-monitor-scroll-content">
                    <ScrollContentPane data-testid="staff-import-monitor-scroll-pane">
                        <MonitorScrollSkeleton />
                    </ScrollContentPane>
                </ScrollingTrack>
                <MonitorTopBar />
            </MonitorIllustration>
            <MonitorStandNeck />
            <MonitorStandBase />
        </IllustrationWrapper>
        <ScanningCopy>
            <ScanningTitle>{title}</ScanningTitle>
            <ScanningMessage>{message}</ScanningMessage>
        </ScanningCopy>
    </ScanningPanelContainer>
);

export default StaffImportScanningPanel;
